import React from 'react';
import { Link } from 'react-router-dom';
import {
  Avatar,
  Box,
  Link as MUILink,
  Tooltip,
  Typography
} from '@mui/material';
import { formatDistance } from 'date-fns';
import { useTheme } from '@mui/system';
import config from '@configFile';
import { formatDateTime } from '@utils/index';
import colors from '@utils/colors';

const AuthorAvatar = () => (
  <MUILink
    component={Link}
    to={config.admin.authorLink}
    rel="author"
    sx={{ ml: 0.75, display: 'inline-flex', alignItems: 'center' }}
    onClick={() =>
      requestAnimationFrame(() =>
        window.scrollTo({ top: 0, behavior: 'smooth' })
      )
    }
  >
    <Avatar
      src={config.admin.image}
      alt={`${config.admin.firstName} ${config.admin.lastName}`}
      sx={{ width: 22, height: 22 }}
    />
  </MUILink>
);

const PosterDetails = ({ postDate }: { postDate?: string | null }) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  const additional: React.CSSProperties = {};

  if (isDarkMode) {
    additional.color = colors.posterDetailsAccent;
  }

  if (!postDate) {
    return (
      <Typography
        variant="caption"
        sx={{ display: 'flex', alignItems: 'center' }}
      >
        <Box sx={{ display: 'inline', mr: 0.5 }}>{`Posted by `}</Box>
        <MUILink
          component={Link}
          to={config.admin.authorLink}
          sx={{
            fontSize: '0.75rem',
            ...additional
          }}
          rel="author" // Indicates the link points to the author's page
          aria-label={`Author: ${config.admin.firstName} ${config.admin.lastName} ${config.admin.title}`} // Improves accessibility
          onClick={() => {
            // smooth scroll to top
            requestAnimationFrame(() =>
              window.scrollTo({ top: 0, behavior: 'smooth' })
            );
          }}
        >
          {`${config.admin.firstName} ${config.admin.lastName} | ${config.admin.title}`}
        </MUILink>
        <AuthorAvatar />
      </Typography>
    );
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Tooltip title={`${formatDateTime(new Date(postDate))}`}>
        <span>
          Posted{' '}
          <time dateTime={new Date(postDate).toISOString()}>
            {formatDistance(new Date(postDate), new Date(), {
              addSuffix: true
            })}
          </time>{' '}
          by{' '}
        </span>
      </Tooltip>
      <MUILink
        component={Link}
        to={config.admin.authorLink}
        sx={{
          fontSize: '0.75rem',
          marginLeft: '2px',
          ...additional
        }}
        onClick={() => {
          // smooth scroll to top
          requestAnimationFrame(() =>
            window.scrollTo({ top: 0, behavior: 'smooth' })
          );
        }}
        rel="author" // Indicates the link points to the author's page
        aria-label={`Author: ${config.admin.firstName} | ${config.admin.title}`} // Improves accessibility
      >
        {`${config.admin.firstName} | ${config.admin.title}`}
      </MUILink>
      <AuthorAvatar />
    </Box>
  );
};

export default PosterDetails;
